import React, { useState, useEffect } from 'react';
import { useSession } from '../Utils/SessionContext';
import { API_ENDPOINTS } from '../Utils/ApiConfig';
import toast from 'react-hot-toast';

const EditRoleItem = ({ isOpen, onClose, roleData, onConfirm }) => {
  const { user } = useSession();
  const [mainCategory, setMainCategory] = useState([]);
  const [subCategory, setSubCategory] = useState([]);
  const [newMainCategory, setNewMainCategory] = useState('');
  const [newSubCategory, setNewSubCategory] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);

  // Initialize categories when modal opens
  useEffect(() => {
    if (isOpen && roleData) {
      setMainCategory([...roleData.mainCategory]);
      setSubCategory([...roleData.subCategory]);
    }
  }, [isOpen, roleData]);

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setNewMainCategory('');
      setNewSubCategory('');
    }
  }, [isOpen]);

  const handleAddMainCategory = () => {
    if (newMainCategory.trim() && !mainCategory.includes(newMainCategory.trim())) {
      setMainCategory(prev => [...prev, newMainCategory.trim()]);
      setNewMainCategory('');
    }
  };

  const handleRemoveMainCategory = (index) => {
    setMainCategory(prev => prev.filter((_, i) => i !== index));
  };

  const handleAddSubCategory = () => {
    if (newSubCategory.trim() && !subCategory.includes(newSubCategory.trim())) {
      setSubCategory(prev => [...prev, newSubCategory.trim()]);
      setNewSubCategory('');
    }
  };

  const handleRemoveSubCategory = (index) => {
    setSubCategory(prev => prev.filter((_, i) => i !== index));
  };

  const handleConfirm = async () => {
    if (mainCategory.length === 0) {
      toast.error('Please add at least one main category');
      return;
    }
    if (subCategory.length === 0) {
      toast.error('Please add at least one subcategory');
      return;
    }

    if (!user?.access_token) {
      toast.error('Authentication required. Please log in again.');
      return;
    }

    try {
      setIsUpdating(true);

      const requestBody = {
        oldCategories: {
          mainCategory: roleData.mainCategory,
          subCategory: roleData.subCategory
        },
        newCategories: {
          mainCategory: [...mainCategory],
          subCategory: [...subCategory]
        }
      };

      console.log('Updating role item with categories:', requestBody);

      const response = await fetch(API_ENDPOINTS.EDIT_ROLE_ITEM, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${user.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        throw new Error(`Server responded with status: ${response.status}`);
      }

      const data = await response.json();
      console.log('Role item updated successfully:', data);

      // Create the updated role object for local state update
      const updatedRole = {
        ...roleData,
        mainCategory: [...mainCategory],
        subCategory: [...subCategory]
      };

      toast.success('Role item updated successfully!');
      onConfirm(updatedRole);
      onClose();

    } catch (err) {
      console.error('Error updating role item:', err);
      toast.error(`Failed to update role item: ${err.message}`);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleKeyPress = (e, type) => {
    if (e.key === 'Enter') {
      if (type === 'main') {
        handleAddMainCategory();
      } else {
        handleAddSubCategory();
      }
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-semibold text-gray-800">Edit Role Item</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <p className="text-sm text-gray-600 mt-2">
            Modify the categories for this role item. You can add or remove main categories and subcategories.
          </p>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Main Categories */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Main Categories
            </label>
            
            {/* Add new main category */}
            <div className="flex gap-2 mb-3">
              <input
                type="text"
                value={newMainCategory}
                onChange={(e) => setNewMainCategory(e.target.value)}
                onKeyPress={(e) => handleKeyPress(e, 'main')}
                placeholder="Add new main category"
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <button
                onClick={handleAddMainCategory}
                className="px-4 py-2 bg-blue-500 text-white text-sm rounded-md hover:bg-blue-600 transition-colors"
              >
                Add
              </button>
            </div>

            {/* Current main categories */}
            <div className="space-y-2">
              {mainCategory.map((cat, index) => (
                <div key={index} className="flex items-center justify-between bg-gray-50 px-3 py-2 rounded-md">
                  <span className="text-sm text-gray-700">"{cat}"</span>
                  <button
                    onClick={() => handleRemoveMainCategory(index)}
                    className="text-red-500 hover:text-red-700 transition-colors"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              ))}
              {mainCategory.length === 0 && (
                <p className="text-sm text-gray-500 italic">No main categories added</p>
              )}
            </div>
          </div>

          {/* Subcategories */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Subcategories
            </label>
            
            {/* Add new subcategory */}
            <div className="flex gap-2 mb-3">
              <input
                type="text"
                value={newSubCategory}
                onChange={(e) => setNewSubCategory(e.target.value)}
                onKeyPress={(e) => handleKeyPress(e, 'sub')}
                placeholder="Add new subcategory"
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <button
                onClick={handleAddSubCategory}
                className="px-4 py-2 bg-blue-500 text-white text-sm rounded-md hover:bg-blue-600 transition-colors"
              >
                Add
              </button>
            </div>

            {/* Current subcategories */}
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {subCategory.map((sub, index) => (
                <div key={index} className="flex items-center justify-between bg-gray-50 px-3 py-2 rounded-md">
                  <span className="text-sm text-gray-700">• {sub}</span>
                  <button
                    onClick={() => handleRemoveSubCategory(index)}
                    className="text-red-500 hover:text-red-700 transition-colors"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              ))}
              {subCategory.length === 0 && (
                <p className="text-sm text-gray-500 italic">No subcategories added</p>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={isUpdating}
            className={`px-4 py-2 rounded-md transition-colors flex items-center gap-2 ${
              isUpdating
                ? 'bg-blue-400 text-white cursor-not-allowed'
                : 'bg-blue-500 text-white hover:bg-blue-600'
            }`}
          >
            {isUpdating && (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
            )}
            {isUpdating ? 'Updating...' : 'Update Categories'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditRoleItem;
